import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faSave,
  faChevronDown,
  faChevronRight,
  faRefresh,
  faCopy,
  faTrash,
  faPlus,
  faEdit,
  faGripVertical,
  faLock,
  faLockOpen,
  faToggleOn,
  faToggleOff,
  faFileImport,
  faCheck,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';
import { defaultScoringConfig } from '../config/scoringConfig';

// Types
interface ScoringField {
  key: string;
  label: string;
  type: string;
  default: any;
  operation: string | null;
  disabled: boolean;
  required: boolean;
  value?: any;
  locked: boolean;
  active: boolean;
  available: boolean;
  hidden?: boolean;
  frozen?: boolean;
  mutuallyExclusive?: string;
}

interface ScoringSection {
  section: string;
  fields: ScoringField[];
  notes?: string[];
  locked?: boolean;
  active?: boolean;
}

interface ScoringRulesData {
  sections: ScoringSection[];
}

// Generate a key from label
const generateKey = (label: string): string => {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
};

const ScoringRulesConfig: React.FC = () => {
  const [data, setData] = useState<ScoringRulesData>({ sections: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Template State
  const [templates, setTemplates] = useState<{ _id?: string; id?: string; name: string; createdAt?: string }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneTemplateName, setCloneTemplateName] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Add Section Modal
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  // Edit Section Modal
  const [showEditSectionModal, setShowEditSectionModal] = useState(false);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [editingSectionOriginalName, setEditingSectionOriginalName] = useState('');

  // Add/Edit Field Modal
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [fieldModalMode, setFieldModalMode] = useState<'add' | 'edit'>('add');
  const [fieldModalSection, setFieldModalSection] = useState('');
  const [fieldModalData, setFieldModalData] = useState<ScoringField>({
    key: '',
    label: '',
    type: 'number',
    default: 0,
    operation: 'addition',
    disabled: true,
    required: false,
    locked: false,
    active: true,
    available: true
  });
  const [editingFieldOriginalKey, setEditingFieldOriginalKey] = useState('');

  // Notes Modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalSection, setNotesModalSection] = useState('');
  const [notesModalContent, setNotesModalContent] = useState('');

  // Reset to empty
  const resetData = useCallback(() => {
    setData({ sections: [] });
    setExpandedSections(new Set());
    setSelectedTemplateId('');
  }, []);

  // Load default scoring config
  const loadDefaultConfig = useCallback(() => {
    const clonedConfig = JSON.parse(JSON.stringify(defaultScoringConfig));
    setData({ sections: clonedConfig });
    setExpandedSections(new Set(clonedConfig.map((s: any) => s.section)));
    setSelectedTemplateId('');
    toast.success('Default scoring configuration loaded');
  }, []);

  // Load templates list
  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const response = await apiService.getScoringRulesTemplates();
      console.log('Scoring Rules Templates list:', response);
      setTemplates(response || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Load a specific template
  const loadTemplate = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplateId('');
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getScoringRulesTemplate(templateId);
      console.log('Template response:', response);
      if (response) {
        console.log('Scoring template keys:', Object.keys(response));
        // Handle all possible shapes from backend
        let sections: any[] = [];
        if (Array.isArray(response)) {
          sections = response;
        } else if (Array.isArray(response.sections)) {
          sections = response.sections;
        } else if (Array.isArray(response.scoringConfig)) {
          sections = response.scoringConfig;
        } else if (response.data && Array.isArray(response.data.sections)) {
          sections = response.data.sections;
        } else if (response.data && Array.isArray(response.data)) {
          sections = response.data;
        }
        console.log('Parsed scoring sections:', sections.length, sections.map((s: any) => s.section));
        setData({ sections });
        setSelectedTemplateId(templateId);
        setExpandedSections(new Set(sections.map((s: ScoringSection) => s.section)));
        toast.success(`Template "${response.name || 'Loaded'}" loaded`);
      }
    } catch (err: any) {
      console.error('Error loading template:', err);
      toast.error(`Failed to load template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle save
  const handleSave = () => {
    if (data.sections.length === 0) {
      toast.error('Add at least one section before saving');
      return;
    }
    if (selectedTemplateId) {
      updateExistingTemplate();
    } else {
      setShowSaveModal(true);
    }
  };

  // Update existing template
  const updateExistingTemplate = async () => {
    try {
      setSaving(true);
      const template = templates.find(t => (t._id || t.id) === selectedTemplateId);
      await apiService.updateScoringRulesTemplate(selectedTemplateId, template?.name || 'Unnamed', { scoringConfig: data.sections });
      toast.success('Template updated successfully');
      loadTemplates();
    } catch (err: any) {
      console.error('Error updating template:', err);
      toast.error(`Failed to update template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Save new template
  const saveNewTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.saveScoringRulesTemplate(templateName, { scoringConfig: data.sections });
      toast.success('Template saved successfully');
      setShowSaveModal(false);
      setTemplateName('');
      loadTemplates();
      const newId = response?._id || response?.id;
      if (newId) {
        setSelectedTemplateId(newId);
      }
    } catch (err: any) {
      console.error('Error saving template:', err);
      toast.error(`Failed to save template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Clone template
  const handleClone = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template to clone');
      return;
    }
    const currentTemplate = templates.find(t => (t._id || t.id) === selectedTemplateId);
    setCloneTemplateName(currentTemplate ? `${currentTemplate.name} (Copy)` : 'New Template');
    setShowCloneModal(true);
  };

  const cloneTemplate = async () => {
    if (!cloneTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.saveScoringRulesTemplate(cloneTemplateName, { scoringConfig: data.sections });
      toast.success('Template cloned successfully');
      setShowCloneModal(false);
      setCloneTemplateName('');
      loadTemplates();
      const newId = response?._id || response?.id;
      if (newId) {
        setSelectedTemplateId(newId);
      }
    } catch (err: any) {
      console.error('Error cloning template:', err);
      toast.error(`Failed to clone template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const deleteTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template to delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      setSaving(true);
      await apiService.deleteScoringRulesTemplate(selectedTemplateId);
      toast.success('Template deleted successfully');
      resetData();
      loadTemplates();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(`Failed to delete template: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  // Add Section
  const addSection = () => {
    if (!newSectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }
    if (data.sections.some(s => s.section.toLowerCase() === newSectionName.toLowerCase())) {
      toast.error('Section already exists');
      return;
    }
    setData(prev => ({
      ...prev,
      sections: [...prev.sections, { section: newSectionName.trim(), fields: [], notes: [], locked: false, active: true }]
    }));
    setExpandedSections(prev => new Set([...Array.from(prev), newSectionName.trim()]));
    setShowAddSectionModal(false);
    setNewSectionName('');
    toast.success('Section added');
  };

  // Toggle Section Locked/Active
  const toggleSectionProperty = (sectionName: string, property: 'locked' | 'active') => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.section === sectionName ? { ...s, [property]: !s[property] } : s
      )
    }));
  };

  // Toggle Field Locked/Active
  const toggleFieldProperty = (sectionName: string, fieldKey: string, property: 'locked' | 'active' | 'available') => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.section !== sectionName) return section;
        return {
          ...section,
          fields: section.fields.map(field =>
            field.key === fieldKey ? { ...field, [property]: !field[property] } : field
          )
        };
      })
    }));
  };

  // Edit Section Name
  const openEditSectionModal = (sectionName: string) => {
    setEditingSectionOriginalName(sectionName);
    setEditingSectionName(sectionName);
    setShowEditSectionModal(true);
  };

  const saveEditSection = () => {
    if (!editingSectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }
    if (editingSectionName !== editingSectionOriginalName &&
        data.sections.some(s => s.section.toLowerCase() === editingSectionName.toLowerCase())) {
      toast.error('Section name already exists');
      return;
    }
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.section === editingSectionOriginalName
          ? { ...s, section: editingSectionName.trim() }
          : s
      )
    }));
    // Update expanded sections
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(editingSectionOriginalName)) {
        next.delete(editingSectionOriginalName);
        next.add(editingSectionName.trim());
      }
      return next;
    });
    setShowEditSectionModal(false);
    toast.success('Section updated');
  };

  // Delete Section
  const deleteSection = (sectionName: string) => {
    if (!window.confirm(`Delete section "${sectionName}" and all its fields?`)) return;
    setData(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.section !== sectionName)
    }));
    toast.success('Section deleted');
  };

  // Open Add Field Modal
  const openAddFieldModal = (sectionName: string) => {
    setFieldModalMode('add');
    setFieldModalSection(sectionName);
    setFieldModalData({
      key: '',
      label: '',
      type: 'number',
      default: 0,
      operation: 'addition',
      disabled: true,
      required: false,
      locked: false,
      active: true,
      available: true
    });
    setShowFieldModal(true);
  };

  // Open Edit Field Modal
  const openEditFieldModal = (sectionName: string, field: ScoringField) => {
    setFieldModalMode('edit');
    setFieldModalSection(sectionName);
    setEditingFieldOriginalKey(field.key);
    setFieldModalData({ ...field });
    setShowFieldModal(true);
  };

  // Save Field (Add or Edit)
  const saveField = () => {
    if (!fieldModalData.label.trim()) {
      toast.error('Please enter a field label');
      return;
    }

    const key = fieldModalData.key.trim() || generateKey(fieldModalData.label);
    const section = data.sections.find(s => s.section === fieldModalSection);

    if (fieldModalMode === 'add') {
      if (section?.fields.some(f => f.key === key)) {
        toast.error('Field key already exists in this section');
        return;
      }
    } else {
      if (key !== editingFieldOriginalKey && section?.fields.some(f => f.key === key)) {
        toast.error('Field key already exists in this section');
        return;
      }
    }

    const newField: ScoringField = {
      ...fieldModalData,
      key,
      label: fieldModalData.label.trim(),
      default: fieldModalData.type === 'boolean'
        ? Boolean(fieldModalData.default)
        : Number(fieldModalData.default) || 0,
      value: fieldModalData.type === 'boolean'
        ? Boolean(fieldModalData.default)
        : Number(fieldModalData.default) || 0
    };

    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.section !== fieldModalSection) return s;
        if (fieldModalMode === 'add') {
          return { ...s, fields: [...s.fields, newField] };
        } else {
          return {
            ...s,
            fields: s.fields.map(f => f.key === editingFieldOriginalKey ? newField : f)
          };
        }
      })
    }));

    setShowFieldModal(false);
    toast.success(fieldModalMode === 'add' ? 'Field added' : 'Field updated');
  };

  // Delete Field
  const deleteField = (sectionName: string, fieldKey: string) => {
    if (!window.confirm('Delete this field?')) return;
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.section !== sectionName) return s;
        return { ...s, fields: s.fields.filter(f => f.key !== fieldKey) };
      })
    }));
    toast.success('Field deleted');
  };

  // Update field value
  const updateFieldValue = (sectionName: string, fieldKey: string, value: number | boolean) => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.section !== sectionName) return section;
        return {
          ...section,
          fields: section.fields.map(field => {
            if (field.key !== fieldKey) return field;
            return { ...field, value, default: value };
          })
        };
      })
    }));
  };

  // Notes Modal
  const openNotesModal = (sectionName: string, notes: string[]) => {
    setNotesModalSection(sectionName);
    setNotesModalContent(notes.join('\n'));
    setShowNotesModal(true);
  };

  const saveNotes = () => {
    const notes = notesModalContent.split('\n').filter(n => n.trim());
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.section === notesModalSection ? { ...s, notes } : s
      )
    }));
    setShowNotesModal(false);
    toast.success('Notes updated');
  };

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  return (
    <div className="p-6 bg-gray-50 dark:bg-dark-600 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg shadow-lg">
              <FontAwesomeIcon icon={faCog} className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Scoring Rules Config</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Build and manage scoring rules templates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Template Selection & Actions */}
      <div className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Template Dropdown */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Load Template
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => loadTemplate(e.target.value)}
              disabled={loadingTemplates || loading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">-- Select Template --</option>
              {templates.map((template) => (
                <option key={template._id || template.id} value={template._id || template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex items-end gap-2 flex-wrap">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} />
              {selectedTemplateId ? 'Update' : 'Save'}
            </button>
            <button
              onClick={handleClone}
              disabled={!selectedTemplateId || saving || loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faCopy} />
              Clone
            </button>
            <button
              onClick={deleteTemplate}
              disabled={!selectedTemplateId || saving || loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete
            </button>
            <button
              onClick={resetData}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faRefresh} />
              Clear
            </button>
            <button
              onClick={loadDefaultConfig}
              disabled={loading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              title="Load default scoring configuration"
            >
              <FontAwesomeIcon icon={faFileImport} />
              Load Default
            </button>
          </div>
        </div>
      </div>

      {/* Add Section Button */}
      <div className="mb-4">
        <button
          onClick={() => setShowAddSectionModal(true)}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} />
          Add Section
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && data.sections.length === 0 && (
        <div className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-12 text-center">
          <FontAwesomeIcon icon={faCog} className="text-gray-300 dark:text-gray-600 text-5xl mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Sections Yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">Start building your scoring rules by adding a section.</p>
          <button
            onClick={() => setShowAddSectionModal(true)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add First Section
          </button>
        </div>
      )}

      {/* Scoring Rules Sections */}
      {!loading && data.sections.length > 0 && (
        <div className="space-y-4">
          {data.sections.map((section) => (
            <div
              key={section.section}
              className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 overflow-hidden"
            >
              {/* Section Header */}
              <div className={`flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-dark-400 ${!section.active ? 'opacity-50' : ''}`}>
                <div
                  className="flex items-center gap-3 cursor-pointer flex-1"
                  onClick={() => toggleSection(section.section)}
                >
                  <FontAwesomeIcon icon={faGripVertical} className="text-gray-400 cursor-grab" />
                  <FontAwesomeIcon
                    icon={expandedSections.has(section.section) ? faChevronDown : faChevronRight}
                    className="text-gray-500 dark:text-gray-400 w-4"
                  />
                  <h3 className="font-semibold text-gray-900 dark:text-white">{section.section}</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-dark-600 px-2 py-0.5 rounded-full">
                    {section.fields.length} fields
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Active Toggle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSectionProperty(section.section, 'active'); }}
                    className={`p-1.5 rounded transition-colors ${section.active ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    title={section.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    <FontAwesomeIcon icon={section.active ? faToggleOn : faToggleOff} className="w-5 h-5" />
                  </button>
                  {/* Locked Toggle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSectionProperty(section.section, 'locked'); }}
                    className={`p-1.5 rounded transition-colors ${section.locked ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    title={section.locked ? 'Locked - Click to unlock' : 'Unlocked - Click to lock'}
                  >
                    <FontAwesomeIcon icon={section.locked ? faLock : faLockOpen} className="w-4 h-4" />
                  </button>
                  <div className="w-px h-5 bg-gray-300 dark:bg-dark-300 mx-1" />
                  <button
                    onClick={(e) => { e.stopPropagation(); openNotesModal(section.section, section.notes || []); }}
                    className="p-1.5 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded"
                    title="Edit notes"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditSectionModal(section.section); }}
                    className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                    title="Edit section"
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSection(section.section); }}
                    className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    title="Delete section"
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Section Content */}
              {expandedSections.has(section.section) && (
                <div className="p-4">
                  {/* Notes */}
                  {section.notes && section.notes.length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                      <ul className="text-sm text-amber-800 dark:text-amber-300 list-disc list-inside">
                        {section.notes.map((note, idx) => (
                          <li key={idx}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Add Field Button */}
                  <div className="mb-4">
                    <button
                      onClick={() => openAddFieldModal(section.section)}
                      className="px-3 py-1.5 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faPlus} className="text-xs" />
                      Add Field
                    </button>
                  </div>

                  {/* Fields */}
                  {section.fields.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No fields yet. Add your first field.</p>
                  ) : (
                    <div className="space-y-3">
                      {section.fields.map((field) => (
                        <div
                          key={field.key}
                          className={`p-4 bg-gray-50 dark:bg-dark-600 rounded-lg border border-gray-200 dark:border-dark-400 ${!field.active ? 'opacity-50' : ''} ${field.locked ? 'border-l-4 border-l-red-500' : ''} ${field.available === false ? 'opacity-30 bg-gray-100 dark:bg-dark-700' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              {/* Field Info */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Label</p>
                                <p className="font-medium text-gray-900 dark:text-white">{field.label}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Key: {field.key}</p>
                              </div>

                              {/* Type & Operation */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Type / Operation</p>
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-0.5 text-xs rounded bg-gray-200 dark:bg-dark-400 text-gray-700 dark:text-gray-300">
                                    {field.type}
                                  </span>
                                  {field.operation && (
                                    <span className={`px-2 py-0.5 text-xs rounded ${
                                      field.operation === 'multiplier'
                                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                        : field.operation === 'substract'
                                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    }`}>
                                      {field.operation === 'multiplier' ? '×' : field.operation === 'substract' ? '−' : '+'}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Flags */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Flags</p>
                                <div className="flex items-center gap-2">
                                  {field.required && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Required</span>
                                  )}
                                  {field.disabled && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400">Disabled</span>
                                  )}
                                  {field.available === false && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">Unavailable</span>
                                  )}
                                  {!field.required && !field.disabled && field.available !== false && (
                                    <span className="text-xs text-gray-400">None</span>
                                  )}
                                </div>
                              </div>

                              {/* Value Input */}
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Default Value</p>
                                {field.type === 'boolean' ? (
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(field.value ?? field.default)}
                                      onChange={(e) => updateFieldValue(section.section, field.key, e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                                  </label>
                                ) : field.type === 'strikeRate' || field.type === 'economyRate' ? (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                    {(() => {
                                      const val = (field.value ?? field.default) as any;
                                      if (!val || typeof val !== 'object') return <span>Not configured</span>;
                                      return (
                                        <>
                                          <p>Min {field.type === 'strikeRate' ? 'Balls' : 'Overs'}: <span className="font-medium text-gray-700 dark:text-gray-300">{val.minBalls ?? val.minOvers ?? '—'}</span></p>
                                          {val.ranges?.map((r: any, i: number) => (
                                            <p key={i}>
                                              {r.type === 'above' ? '>' : '<'} {r.threshold}: <span className={`font-medium ${r.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>{r.points > 0 ? '+' : ''}{r.points} pts</span>
                                            </p>
                                          ))}
                                        </>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <input
                                    type="number"
                                    step={field.operation === 'multiplier' ? '0.1' : '1'}
                                    value={Number(field.value ?? field.default)}
                                    onChange={(e) => updateFieldValue(section.section, field.key, parseFloat(e.target.value) || 0)}
                                    className="w-full max-w-[120px] px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                  />
                                )}
                              </div>
                            </div>

                            {/* Field Actions */}
                            <div className="flex items-center gap-1">
                              {/* Active Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'active')}
                                className={`p-1.5 rounded transition-colors ${field.active ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={field.active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                              >
                                <FontAwesomeIcon icon={field.active ? faToggleOn : faToggleOff} className="w-4 h-4" />
                              </button>
                              {/* Locked Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'locked')}
                                className={`p-1.5 rounded transition-colors ${field.locked ? 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                title={field.locked ? 'Locked - Click to unlock' : 'Unlocked - Click to lock'}
                              >
                                <FontAwesomeIcon icon={field.locked ? faLock : faLockOpen} className="w-3.5 h-3.5" />
                              </button>
                              {/* Available Toggle */}
                              <button
                                onClick={() => toggleFieldProperty(section.section, field.key, 'available')}
                                className={`p-1.5 rounded transition-colors ${field.available === false ? 'text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30' : 'text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'}`}
                                title={field.available === false ? 'Unavailable for series - Click to make available' : 'Available for series - Click to make unavailable'}
                              >
                                <FontAwesomeIcon icon={field.available === false ? faBan : faCheck} className="w-3.5 h-3.5" />
                              </button>
                              <div className="w-px h-4 bg-gray-300 dark:bg-dark-300 mx-0.5" />
                              <button
                                onClick={() => openEditFieldModal(section.section, field)}
                                className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                                title="Edit field"
                              >
                                <FontAwesomeIcon icon={faEdit} className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteField(section.section, field.key)}
                                className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                title="Delete field"
                              >
                                <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Section</h3>
            <input
              type="text"
              placeholder="Section name (e.g., Batting, Bowling)"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && addSection()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowAddSectionModal(false); setNewSectionName(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={addSection}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Section Modal */}
      {showEditSectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Section</h3>
            <input
              type="text"
              placeholder="Section name"
              value={editingSectionName}
              onChange={(e) => setEditingSectionName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveEditSection()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowEditSectionModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveEditSection}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Field Modal */}
      {showFieldModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {fieldModalMode === 'add' ? 'Add Field' : 'Edit Field'}
            </h3>

            <div className="space-y-4">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Per Run, Century Bonus"
                  value={fieldModalData.label}
                  onChange={(e) => setFieldModalData(prev => ({ ...prev, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key <span className="text-gray-400 text-xs">(auto-generated if empty)</span>
                </label>
                <input
                  type="text"
                  placeholder={generateKey(fieldModalData.label) || 'e.g., per_run'}
                  value={fieldModalData.key}
                  onChange={(e) => setFieldModalData(prev => ({ ...prev, key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select
                  value={fieldModalData.type}
                  onChange={(e) => setFieldModalData(prev => ({
                    ...prev,
                    type: e.target.value,
                    default: e.target.value === 'boolean' ? false : 0,
                    operation: e.target.value === 'boolean' ? null : prev.operation
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="strikeRate">Strike Rate</option>
                  <option value="economyRate">Economy Rate</option>
                </select>
              </div>

              {/* Operation (only for number type) */}
              {fieldModalData.type === 'number' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Operation</label>
                  <select
                    value={fieldModalData.operation || 'addition'}
                    onChange={(e) => setFieldModalData(prev => ({
                      ...prev,
                      operation: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="addition">Addition (+)</option>
                    <option value="substract">Subtract (-)</option>
                    <option value="multiplier">Multiplier (x)</option>
                  </select>
                </div>
              )}

              {/* Default Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Value</label>
                {fieldModalData.type === 'boolean' ? (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(fieldModalData.default)}
                      onChange={(e) => setFieldModalData(prev => ({ ...prev, default: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">
                      {fieldModalData.default ? 'Yes' : 'No'}
                    </span>
                  </label>
                ) : (
                  <input
                    type="number"
                    step={fieldModalData.operation === 'multiplier' ? '0.1' : '1'}
                    value={Number(fieldModalData.default)}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, default: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                )}
              </div>

              {/* Flags */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldModalData.required}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, required: e.target.checked }))}
                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldModalData.disabled}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, disabled: e.target.checked }))}
                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Disabled</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldModalData.active}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, active: e.target.checked }))}
                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldModalData.locked}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, locked: e.target.checked }))}
                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Locked</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fieldModalData.available ?? true}
                    onChange={(e) => setFieldModalData(prev => ({ ...prev, available: e.target.checked }))}
                    className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Available</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowFieldModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveField}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                {fieldModalMode === 'add' ? 'Add Field' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-lg shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Edit Notes - {notesModalSection}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">One note per line</p>
            <textarea
              value={notesModalContent}
              onChange={(e) => setNotesModalContent(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Enter notes, one per line..."
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveNotes}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Save Template</h3>
            <input
              type="text"
              placeholder="Template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveNewTemplate()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowSaveModal(false); setTemplateName(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveNewTemplate}
                disabled={saving}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Template Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-500 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Clone Template</h3>
            <input
              type="text"
              placeholder="New template name"
              value={cloneTemplateName}
              onChange={(e) => setCloneTemplateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-400 rounded-lg bg-white dark:bg-dark-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && cloneTemplate()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowCloneModal(false); setCloneTemplateName(''); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-400 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={cloneTemplate}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Cloning...' : 'Clone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* JSON Preview (collapsible) */}
      <details className="bg-white dark:bg-dark-500 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 mt-6">
        <summary className="p-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors rounded-xl">
          JSON Preview
        </summary>
        <pre className="p-4 bg-gray-900 text-green-400 text-xs overflow-auto max-h-96 rounded-b-xl">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default ScoringRulesConfig;
